// Completude de step e regras de liberação linear (form §1.2 / §7.6).
//
// A navegação do modal é linear: um step só é liberado quando **todos** os
// anteriores estão completos. Completude é derivada das respostas persistidas
// (`InspectionDetail.itemResponses`), nunca de estado local de UI — retomar a
// ficha em outro dia/dispositivo tem de reabrir no mesmo ponto.

import type {
  InspectionAttachment,
  InspectionDetail,
  InspectionItemResponseDto,
} from "../../api/inspection";
import { INSPECTION_CHECKLIST_STEPS } from "./inspection-form.catalog";
import type { InspectionItemDef, InspectionStepDef } from "./inspection-form.types";
import { INSPECTION_FORM_STEPS } from "./inspection-steps";
import { ecItemKey } from "../inspection-ec-columns";
import {
  anomalousGradeFieldsFromAnswer,
  gradeEvidenceOccurrenceKey,
  readGradeObservation,
} from "./inspection-grade-evidence";

const GENERAL_COMMENTS_ITEM_KEY = "generalComments";
const EC_RESPONSE_KEYS = ["ecK", "ecL", "ecM", "ecN", "ecO"];

type ResponseIndex = Map<string, InspectionItemResponseDto>;

const indexOf = (responses: InspectionItemResponseDto[]): ResponseIndex => {
  const index: ResponseIndex = new Map();

  if (!Array.isArray(responses)) return index;

  for (const response of responses) {
    if ((response.occurrenceKey ?? "") !== "") continue;
    index.set(response.itemKey, response);
  }

  return index;
};

const answerFor = (index: ResponseIndex, itemKey: string) =>
  (index.get(itemKey)?.answerValue ?? null) as Record<string, unknown> | null;

const hasAnswer = (index: ResponseIndex, itemKey: string) => {
  const answer = answerFor(index, itemKey);
  if (!answer) return false;

  return Object.values(answer).some(
    (value) => value !== null && value !== undefined && value !== "",
  );
};

const optionValue = (index: ResponseIndex, itemKey: string) => {
  const value = answerFor(index, itemKey)?.value;
  return typeof value === "string" ? value : undefined;
};

/** Item exigido para concluir o step (form §7.6). */
const isItemRequired = (item: InspectionItemDef) => {
  if (item.optional) return false;
  if (item.options) return true;

  // Itens descritivos com campo obrigatório sempre visível (ex.: tipo de
  // extravasor) também travam a conclusão do step.
  return (item.fields ?? []).some((field) => field.required === true && !field.when);
};

const isItemVisible = (item: InspectionItemDef, index: ResponseIndex) => {
  if (!item.visibleWhen) return true;
  const parent = optionValue(index, item.visibleWhen.itemKey);
  return parent !== undefined && item.visibleWhen.values.includes(parent);
};

const isItemComplete = (item: InspectionItemDef, index: ResponseIndex): boolean => {
  if (!isItemVisible(item, index)) return true;
  if (isItemRequired(item) && !hasAnswer(index, item.itemKey)) return false;

  const selected = optionValue(index, item.itemKey);
  const dismissed = Boolean(
    item.options?.find((option) => option.value === selected)?.dismisses,
  );

  if (dismissed || (item.naCheckbox && selected === "notApplicable")) return true;

  const answer = answerFor(index, item.itemKey);
  for (const field of item.fields ?? []) {
    if (field.required !== true) continue;
    if (field.when && (selected === undefined || !field.when.includes(selected))) {
      continue;
    }
    const fieldValue = answer?.[field.key];
    if (fieldValue === null || fieldValue === undefined || fieldValue === "") {
      return false;
    }
  }

  const nestedVisible =
    item.nested &&
    selected !== undefined &&
    (item.nestedWhen ?? []).includes(selected);

  if (nestedVisible) {
    return (item.nested ?? []).every((nested) => isItemComplete(nested, index));
  }

  return true;
};

const isChecklistStepComplete = (
  step: InspectionStepDef,
  index: ResponseIndex,
): boolean => {
  if (step.gate) {
    if (!hasAnswer(index, step.gate.itemKey)) return false;
    if (optionValue(index, step.gate.itemKey) !== "yes") return true;
  }

  return step.items.every((item) => isItemComplete(item, index));
};

const belongsToStep = (itemKey: string, stepKey: string) =>
  itemKey === stepKey || itemKey.startsWith(`${stepKey}.`);

export const isInspectionStepComplete = (
  stepKey: string,
  responses: InspectionItemResponseDto[],
  attachments: InspectionAttachment[] = [],
): boolean => {
  const index = indexOf(responses);
  const checklistStep = INSPECTION_CHECKLIST_STEPS[stepKey];

  if (checklistStep) {
    if (!isChecklistStepComplete(checklistStep, index)) return false;
    return !hasAnomalyWithoutEvidence(
      responses.filter((response) => belongsToStep(response.itemKey, stepKey)),
      attachments,
    );
  }

  if (stepKey === "conservationState") {
    return EC_RESPONSE_KEYS.every((responseKey) => {
      const answer = answerFor(index, ecItemKey(responseKey));
      return typeof answer?.selectedScore === "number";
    });
  }

  // `generalComments` é opcional: basta o step ter sido salvo (a linha existe,
  // mesmo com texto vazio — form §7.3.10).
  if (stepKey === "generalComments") return index.has(GENERAL_COMMENTS_ITEM_KEY);

  // `generalData` vem do POST (form §7.2) e `signatures` é o último step.
  return true;
};

/**
 * Quantidade de steps navegáveis: todos os completos + o primeiro incompleto
 * (o step corrente). Steps à frente ficam travados até o corrente ser salvo.
 */
export const unlockedInspectionStepCount = (
  responses: InspectionItemResponseDto[],
  attachments: InspectionAttachment[] = [],
): number => {
  for (let index = 0; index < INSPECTION_FORM_STEPS.length; index += 1) {
    const step = INSPECTION_FORM_STEPS[index]!;
    if (!isInspectionStepComplete(step.stepKey, responses, attachments)) {
      return index + 1;
    }
  }

  return INSPECTION_FORM_STEPS.length;
};

export const isInspectionChecklistComplete = (
  responses: InspectionItemResponseDto[],
  attachments: InspectionAttachment[] = [],
): boolean =>
  INSPECTION_FORM_STEPS.every((step) =>
    isInspectionStepComplete(step.stepKey, responses, attachments),
  );

/**
 * Resposta em anomalia sem evidência (form §5.1): observação obrigatória e
 * pelo menos uma foto. Bloqueia o botão Finalizar antes de bater no backend.
 */
export const hasAnomalyWithoutEvidence = (
  responses: InspectionItemResponseDto[],
  attachments: InspectionAttachment[],
): boolean =>
  (Array.isArray(responses) ? responses : []).some((response) => {
    if (response.anomalyContribution <= 0) return false;

    const occurrenceKey = response.occurrenceKey ?? "";
    const answer = (response.answerValue ?? null) as Record<
      string,
      unknown
    > | null;
    const graded = anomalousGradeFieldsFromAnswer(answer);
    const sharedObservations = Boolean(response.observations?.trim());
    const sharedPhoto = attachments.some(
      (attachment) =>
        attachment.itemKey === response.itemKey &&
        (attachment.occurrenceKey ?? "") === occurrenceKey,
    );

    if (graded.length > 0) {
      return graded.some((field) => {
        const hasObs =
          Boolean(readGradeObservation(answer, field)) || sharedObservations;
        const hasPhoto =
          attachments.some(
            (attachment) =>
              attachment.itemKey === response.itemKey &&
              (attachment.occurrenceKey ?? "") ===
                gradeEvidenceOccurrenceKey(occurrenceKey, field),
          ) || sharedPhoto;
        return !hasObs || !hasPhoto;
      });
    }

    return !sharedObservations || !sharedPhoto;
  });

/** Finalizar exige checklist completo, evidência de anomalia e assinaturas. */
export const canFinalizeInspection = (detail: InspectionDetail): boolean => {
  if (detail.inspectionStatus !== "inProgress" && detail.inspectionStatus !== "open") {
    return false;
  }

  if (!isInspectionChecklistComplete(detail.itemResponses, detail.attachments)) {
    return false;
  }
  if (hasAnomalyWithoutEvidence(detail.itemResponses, detail.attachments)) {
    return false;
  }

  return (
    detail.participants.length > 0 &&
    detail.participants.every((participant) => participant.hasSignature)
  );
};

/** ∑EC das respostas do step `conservationState` (form §7.4). */
export const inspectionConservationStateScore = (
  responses: InspectionItemResponseDto[],
): number | null => {
  const index = indexOf(responses);
  let total = 0;

  for (const responseKey of EC_RESPONSE_KEYS) {
    const answer = answerFor(index, ecItemKey(responseKey));
    if (typeof answer?.selectedScore !== "number") return null;
    total += answer.selectedScore;
  }

  return total;
};
